import {
  FilterExpr,
  FilterLiteral,
  FilterPredicate,
  fieldPath,
  pathKey
} from '@core/filters/path-filter/wire';
import { FilterConnector } from '@core/models/entity/entity-selector-request.model';
import { FilterPresetPayload } from '@core/models/filter-preset.model';

import {
  FilterCondition,
  FilterConfiguration,
  FilterDataType,
  FilterGroup,
  FilterOperation
} from '@shared/components/filter-query-builder/filter.models';
import {
  conditionIsFilled,
  generateFilterId,
  orConditionIntoConfig,
  uidListCondition
} from '@shared/utils/filter-condition.utils';

/**
 * Whether a reference popover offers to add its row to the table's filter.
 * `blocked-free-text`: free-text terms travel as the request's `search`, ANDed with every
 * filter, so they would keep hiding the row; they cannot be turned into a condition.
 */
export type IncludeInFilterStatus =
  | 'hidden'
  | 'available'
  | 'already-shown'
  | 'blocked-free-text';

/**
 * Only a reference to the entity the table lists can be included, since including means
 * "also show this row".
 *
 * @param payload The table's effective filter, search-bar qualifiers already folded in: a Path
 *   payload carries its tree as `pathFilter` and no `filters`.
 */
export function includeInFilterStatus(
  payload: FilterPresetPayload,
  hostEntityType: string,
  targetEntityType: string,
  uid: string
): IncludeInFilterStatus {
  if (hostEntityType.toUpperCase() !== targetEntityType.toUpperCase()) return 'hidden';
  if (payload.search) return 'blocked-free-text';
  const rootVerdict = payload.pathFilter
    ? pathVerdictFor(payload.pathFilter, uid)
    : payload.filters
      ? verdictFor(payload.filters.rootGroup, uid)
      : 'absent';
  return rootVerdict === 'maybe-hidden' ? 'available' : 'already-shown';
}

/** `payload`'s filter OR `uid = <uid>`, to become the table's advanced filter. */
export function includeUidInFilter(
  payload: FilterPresetPayload,
  uid: string,
  targetEntityType: string
): FilterConfiguration {
  return orConditionIntoConfig(
    payload.filters,
    uidListCondition(generateFilterId(), 'uid', [uid], targetEntityType.toUpperCase())
  );
}

/**
 * What a filter item does to the row `uid`, judged the way the request is built: disabled and
 * unfilled conditions are left out of it, so they count as `absent`, as does a group holding
 * nothing else. Judging the tree rather than the shape `orConditionIntoConfig` leaves keeps an
 * include recognised wherever the user moves it, and keeps a filter that narrows nothing from
 * being widened into one that shows only `uid`.
 */
type Verdict = 'absent' | 'shown' | 'maybe-hidden';

function verdictFor(item: FilterCondition | FilterGroup, uid: string): Verdict {
  if ('conditions' in item) {
    const verdicts = item.conditions
      .map((child) => verdictFor(child, uid))
      .filter((verdict) => verdict !== 'absent');
    if (verdicts.length === 0) return 'absent';
    const shown =
      item.connector === FilterConnector.OR
        ? verdicts.includes('shown')
        : verdicts.every((verdict) => verdict === 'shown');
    return shown ? 'shown' : 'maybe-hidden';
  }
  if (!item.enabled || !conditionIsFilled(item)) return 'absent';
  return item.field === 'uid' &&
    item.dataType === FilterDataType.UID_LIST &&
    item.operation === FilterOperation.IS_ANY_OF &&
    (item.values ?? []).includes(uid)
    ? 'shown'
    : 'maybe-hidden';
}

const ROOT_UID = pathKey(fieldPath('uid'));

/**
 * `verdictFor` on a Path tree, which holds no disabled or unfilled conditions. A negation or an
 * association may hide the row whatever it holds: judging what it does to `uid` would mean
 * evaluating it.
 */
function pathVerdictFor(expr: FilterExpr, uid: string): Verdict {
  if ('and' in expr || 'or' in expr) {
    const isOr = 'or' in expr;
    const verdicts = (isOr ? expr.or : expr.and)
      .map((child) => pathVerdictFor(child, uid))
      .filter((verdict) => verdict !== 'absent');
    if (verdicts.length === 0) return 'absent';
    const shown = isOr
      ? verdicts.includes('shown')
      : verdicts.every((verdict) => verdict === 'shown');
    return shown ? 'shown' : 'maybe-hidden';
  }
  if ('predicate' in expr) return namesUid(expr.predicate, uid) ? 'shown' : 'maybe-hidden';
  return 'maybe-hidden';
}

function namesUid({ path, op }: FilterPredicate, uid: string): boolean {
  if (pathKey(path) !== ROOT_UID || typeof op === 'string') return false;
  const isUid = (literal: FilterLiteral) =>
    typeof literal === 'object' &&
    (('uid' in literal && literal.uid === uid) ||
      ('string' in literal && literal.string === uid));
  if ('is_any_of' in op) return op.is_any_of.some(isUid);
  if ('equals' in op) return isUid(op.equals);
  return false;
}
